// Deterministic, low-cost hash → HSL hue mapping so every test gets the same
// chip color everywhere it's referenced. Not crypto — just a stable spread.
//
// cyrb53 is a small, well-distributed 53-bit hash that fits comfortably in a
// JS number. We only need ~9 bits of entropy (one of 360 hues) so this is
// massive overkill, which is the point: collisions across the ~30-50 tests a
// recruiter realistically has are negligible.
export function cyrb53(str: string, seed = 0): number {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0, ch: number; i < str.length; i++) {
    ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

export interface TestColor {
  /** Solid colored background for accent surfaces (active pills). */
  bg: string;
  /** Soft tinted background for chips at rest. */
  bgSoft: string;
  /** Foreground / text color paired with bgSoft. */
  fg: string;
  /** Border for cards/pills (matches bgSoft contrast). */
  border: string;
  /** Ring used to indicate active filter on a chip. */
  ring: string;
}

const cache = new Map<string, TestColor>();

export function testColor(testId: string): TestColor {
  const cached = cache.get(testId);
  if (cached) return cached;

  // 0–359 hue. Avoid red→orange clash with the `destructive` token by skipping
  // hues 350–15 — those are reserved for error states.
  let hue = cyrb53(testId) % 360;
  if (hue < 16) hue += 16;
  if (hue > 349) hue -= 16;

  const color: TestColor = {
    bg: `hsl(${hue} 60% 45%)`,
    bgSoft: `hsl(${hue} 70% 94%)`,
    fg: `hsl(${hue} 55% 25%)`,
    border: `hsl(${hue} 50% 80%)`,
    ring: `hsl(${hue} 55% 50%)`,
  };
  cache.set(testId, color);
  return color;
}

const tagCache = new Map<string, TestColor>();

// Stable per-tag color, paralleling testColor. Softer saturation + lightness
// than test pills (S=50 L=50 vs test S=60 L=45) so a card crowded with both
// test chips AND tag chips reads test-identity-first.
export function tagColor(tagId: string): TestColor {
  const cached = tagCache.get(tagId);
  if (cached) return cached;
  let hue = cyrb53(tagId) % 360;
  if (hue < 16) hue += 16;
  if (hue > 349) hue -= 16;
  const color: TestColor = {
    bg: `hsl(${hue} 50% 50%)`,
    bgSoft: `hsl(${hue} 60% 95%)`,
    fg: `hsl(${hue} 45% 30%)`,
    border: `hsl(${hue} 45% 82%)`,
    ring: `hsl(${hue} 50% 55%)`,
  };
  tagCache.set(tagId, color);
  return color;
}

const jobCache = new Map<string, TestColor>();

// Stable per-job color, paralleling testColor + tagColor. BOLDER than test
// pills (S=65 L=40 vs test S=60 L=45) since jobs are the structural parent
// in the hierarchy (Jobs → Tests → Questions → Tags) and should visually
// out-saturate the entities nested under them.
export function jobColor(jobId: string): TestColor {
  const cached = jobCache.get(jobId);
  if (cached) return cached;
  let hue = cyrb53(jobId) % 360;
  if (hue < 16) hue += 16;
  if (hue > 349) hue -= 16;
  const color: TestColor = {
    bg: `hsl(${hue} 65% 40%)`,
    bgSoft: `hsl(${hue} 75% 93%)`,
    fg: `hsl(${hue} 60% 22%)`,
    border: `hsl(${hue} 55% 78%)`,
    ring: `hsl(${hue} 60% 45%)`,
  };
  jobCache.set(jobId, color);
  return color;
}
